import { useState, useCallback, useMemo } from 'react';
import { Track } from '@shared/schema';

type FilterState = {
  useCase: string | null;
  genre: string | null;
  mood: string | null;
};

export function useTrackFilter(tracks: Track[]) {
  const [filters, setFilters] = useState<FilterState>({
    useCase: null,
    genre: null,
    mood: null,
  });
  const [searchQuery, setSearchQuery] = useState('');

  const setUseCase = useCallback((useCase: string | null) => {
    setFilters(prev => ({ ...prev, useCase }));
  }, []);

  const setGenre = useCallback((genre: string | null) => {
    setFilters(prev => ({ ...prev, genre }));
  }, []);

  const setMood = useCallback((mood: string | null) => {
    setFilters(prev => ({ ...prev, mood }));
  }, []);

  const filterTracks = useCallback(() => {
    if (!tracks) return [];

    return tracks.filter(track => {
      // Filter by use case
      if (filters.useCase && track.useCase !== filters.useCase) {
        return false;
      }

      // Filter by genre
      if (filters.genre && track.genre !== filters.genre) {
        return false;
      }

      // Filter by mood
      if (filters.mood && track.mood !== filters.mood) {
        return false;
      }

      // Search query (case insensitive)
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          track.title.toLowerCase().includes(query) ||
          track.genre.toLowerCase().includes(query) ||
          track.mood.toLowerCase().includes(query) ||
          (track.description && track.description.toLowerCase().includes(query))
        );
      }

      return true;
    });
  }, [tracks, filters, searchQuery]);

  const resetFilters = useCallback(() => {
    setFilters({
      useCase: null,
      genre: null,
      mood: null,
    });
    setSearchQuery('');
  }, []);

  // Calculate unique genres, moods, and use cases from tracks
  const uniqueValues = useMemo(() => {
    if (!tracks || tracks.length === 0) {
      return {
        genres: [],
        moods: [],
        useCases: []
      };
    }

    const genres = Array.from(new Set(tracks.map(track => track.genre)));
    const moods = Array.from(new Set(tracks.map(track => track.mood)));
    const useCases = Array.from(new Set(tracks.map(track => track.useCase)));

    return {
      genres,
      moods,
      useCases
    };
  }, [tracks]);

  return {
    filters,
    searchQuery,
    setUseCase,
    setGenre,
    setMood,
    setSearchQuery,
    filterTracks,
    resetFilters,
    uniqueValues
  };
}
